import { Button, Dropdown, Input, Select, DatePicker, TreeSelect, Tag, Tooltip } from "antd";
import {
    SearchOutlined,
    FilterOutlined,
    UsergroupAddOutlined,
    UserOutlined,
    ClockCircleOutlined,
    EyeOutlined,
    DeleteOutlined,
    PlusOutlined,
    TagOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { mixGetV1 } from "../../services/mixin";
import FilterModal from "./FilterModal";
import CreateCustomerModal from "./CreateCustomerModal";
import { fetchCareStatuses } from "../../pages/RoomChat/chatLabels";

const { Option } = Select;
const { RangePicker } = DatePicker;

type PeriodType = "date" | "week" | "month" | "year";
/** Ngoài các mốc cố định còn có "range": tự chọn khoảng từ ngày - đến ngày */
type PeriodMode = PeriodType | "range";

interface StaffOption {
    label: string;
    value: number;
}

interface SourceConnection {
    id: number;
    name: string;
}

interface SourceGroup {
    id: number;
    name: string;
    connections: SourceConnection[];
}

interface InitialFilters {
    search: string;
    staffId?: number;
    pageStaffId?: number;
    period: string;
    refDate?: string;
    startDate?: string;
    endDate?: string;
    oauthAccountIds: number[];
    channelConnectionIds: number[];
    careStatusIds: number[];
}

export default function CustomerToolbar({ onCreate, onSearch, onStaffChange, onPageStaffChange, onDateChange, onSourceChange, onCareStatusChange, initialFilters }: { onCreate: () => void; onSearch?: (value: string) => void; onStaffChange?: (staffId?: number) => void; onPageStaffChange?: (staffId?: number) => void; onDateChange?: (startDate?: string, endDate?: string, period?: string, refDate?: string) => void; onSourceChange?: (oauthAccountIds: number[], channelConnectionIds: number[]) => void; onCareStatusChange?: (careStatusIds: number[]) => void; initialFilters?: InitialFilters }) {
    const [openCreate, setOpenCreate] = useState(false);
    const { t } = useTranslation();
    const [careStatusOptions, setCareStatusOptions] = useState<{ label: string; value: number; color?: string }[]>([]);
    const [loadingCareStatus, setLoadingCareStatus] = useState(false);
    const [openFilter, setOpenFilter] = useState(false);
    const [openSavedFilter, setOpenSavedFilter] = useState(false);
    const [search, setSearch] = useState("");
    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [loadingStaff, setLoadingStaff] = useState(false);
    // Nhân viên trực page (type_department_id = 2) cho bộ lọc in_charge_staff_id
    const [pageStaffOptions, setPageStaffOptions] = useState<StaffOption[]>([]);
    const [loadingPageStaff, setLoadingPageStaff] = useState(false);
    const [sourceGroups, setSourceGroups] = useState<SourceGroup[]>([]);
    const [loadingSource, setLoadingSource] = useState(false);
    // Dựng lại các control theo bộ lọc khôi phục từ URL (quay lại từ trang chi tiết)
    const [selectedSources, setSelectedSources] = useState<string[]>(() => [
        ...(initialFilters?.oauthAccountIds || []).map((id) => `acc:${id}`),
        ...(initialFilters?.channelConnectionIds || []).map((id) => `conn:${id}`),
    ]);
    const [periodType, setPeriodType] = useState<PeriodMode>((initialFilters?.period as PeriodMode) || "date");
    const [refDate, setRefDate] = useState<Dayjs | null>(
        initialFilters?.refDate ? dayjs(initialFilters.refDate) : null
    );
    // Khoảng tự chọn (từ ngày - đến ngày), khôi phục lại từ URL khi quay lại danh sách
    const [rangeValue, setRangeValue] = useState<[Dayjs | null, Dayjs | null] | null>(
        initialFilters?.period === "range" && initialFilters?.startDate && initialFilters?.endDate
            ? [dayjs(initialFilters.startDate), dayjs(initialFilters.endDate)]
            : null
    );
    const [filters, setFilters] = useState([
        { id: 1, name: "Sử dụng dịch vụ căng chỉ >=1" },
        { id: 2, name: "Sử dụng dịch vụ cấy tóc >=1" },
        { id: 3, name: "Sử dụng dịch vụ Giảm cân >= 1" },
    ]);
    const filteredList = filters.filter((f) =>
        f.name.toLowerCase().includes(search.toLowerCase())
    );

    const handleDelete = (id: number) => {
        setFilters((prev) => prev.filter((f) => f.id !== id));
    };

    useEffect(() => {
        let mounted = true;
        setLoadingStaff(true);
        // type_department_id: 3 = Telesale
        mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 3 })
            .then((res) => {
                if (!mounted) return;
                const rawItems = Array.isArray(res?.data?.items)
                    ? res.data.items
                    : Array.isArray(res?.data)
                        ? res.data
                        : [];
                const options = rawItems
                    .filter((s: any) => s?.id)
                    .map((s: any) => ({
                        label: s?.fullname || s?.name || `#${s.id}`,
                        value: Number(s.id),
                    }));
                setStaffOptions(options);
            })
            .catch(() => undefined)
            .finally(() => {
                if (mounted) setLoadingStaff(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        let mounted = true;
        setLoadingPageStaff(true);
        // type_department_id: 2 = Trực page
        mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 2 })
            .then((res) => {
                if (!mounted) return;
                const rawItems = Array.isArray(res?.data?.items)
                    ? res.data.items
                    : Array.isArray(res?.data)
                        ? res.data
                        : [];
                setPageStaffOptions(
                    rawItems
                        .filter((s: any) => s?.id)
                        .map((s: any) => ({ label: s?.fullname || s?.name || `#${s.id}`, value: Number(s.id) }))
                );
            })
            .catch(() => undefined)
            .finally(() => { if (mounted) setLoadingPageStaff(false); });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        setLoadingSource(true);
        mixGetV1('/api/v1/be/oauth-accounts')
            .then((res) => {
                if (!mounted) return;
                const rawItems = Array.isArray(res?.data?.items)
                    ? res.data.items
                    : Array.isArray(res?.data)
                        ? res.data
                        : [];
                const groups: SourceGroup[] = rawItems
                    .filter((acc: any) => acc?.id)
                    .map((acc: any) => ({
                        id: Number(acc.id),
                        name: acc?.provider || `#${acc.id}`,
                        connections: (Array.isArray(acc?.connections) ? acc.connections : [])
                            .filter((conn: any) => conn?.id)
                            .map((conn: any) => ({
                                id: Number(conn.id),
                                name: conn?.display_name || conn?.external_id || `#${conn.id}`,
                            })),
                    }));
                setSourceGroups(groups);
            })
            .catch(() => undefined)
            .finally(() => {
                if (mounted) setLoadingSource(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    // Dùng lại cache trạng thái chăm sóc của phòng chat, tránh gọi lại API
    useEffect(() => {
        let mounted = true;
        setLoadingCareStatus(true);
        fetchCareStatuses()
            .then((items) => {
                if (!mounted) return;
                setCareStatusOptions(items.map((s) => ({ label: s.name, value: s.id, color: s.color })));
            })
            .catch(() => undefined)
            .finally(() => {
                if (mounted) setLoadingCareStatus(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    const handleSelectFilter = (item: any) => {
        console.log("Selected filter:", item);
        setOpenSavedFilter(false);
    };

    const emitDateRange = (type: PeriodType, date: Dayjs | null) => {
        if (!date) {
            onDateChange?.(undefined, undefined, type, undefined);
            return;
        }
        const start = date.startOf(type).format("YYYY-MM-DD");
        const end = date.endOf(type).format("YYYY-MM-DD");
        onDateChange?.(start, end, type, date.format("YYYY-MM-DD"));
    };

    const handlePeriodTypeChange = (value: PeriodMode) => {
        setPeriodType(value);
        if (value === "range") {
            // Chuyển sang khoảng tự chọn: dùng lại khoảng đã chọn trước đó nếu có
            const [start, end] = rangeValue || [];
            if (start && end) {
                onDateChange?.(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"), "range", undefined);
            } else {
                onDateChange?.(undefined, undefined, "range", undefined);
            }
            return;
        }
        emitDateRange(value, refDate);
    };

    const handleRefDateChange = (date: Dayjs | null) => {
        setRefDate(date);
        emitDateRange(periodType as PeriodType, date);
    };

    const handleRangeChange = (dates: any) => {
        const start = dates?.[0] as Dayjs | undefined;
        const end = dates?.[1] as Dayjs | undefined;
        setRangeValue(dates ? [start || null, end || null] : null);
        if (start && end) {
            onDateChange?.(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"), "range", undefined);
        } else {
            onDateChange?.(undefined, undefined, "range", undefined);
        }
    };

    const handleSourceChange = (values: string[]) => {
        setSelectedSources(values);
        const oauthAccountIds: number[] = [];
        const channelConnectionIds: number[] = [];
        values.forEach((val) => {
            const [type, rawId] = val.split(":");
            const id = Number(rawId);
            if (!id) return;
            if (type === "acc") oauthAccountIds.push(id);
            else if (type === "conn") channelConnectionIds.push(id);
        });
        onSourceChange?.(oauthAccountIds, channelConnectionIds);
    };

    const sourceTreeData = sourceGroups.map((group) => ({
        title: group.name,
        value: `acc:${group.id}`,
        key: `acc:${group.id}`,
        children: group.connections.map((conn) => ({
            title: conn.name,
            value: `conn:${conn.id}`,
            key: `conn:${conn.id}`,
        })),
    }));
    const renderSavedFilter = () => (
        <div className="w-[320px] bg-white dark:bg-[#1f1f1f] rounded-xl shadow-lg border dark:border-[#303030] overflow-hidden">

            {/* SEARCH */}
            <div className="p-3 border-b dark:border-[#303030]">
                <Input
                    placeholder={t('customer.search_filter')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {/* LIST */}
            <div className="max-h-[300px] overflow-y-auto">
                {filteredList.length === 0 && (
                    <div className="p-3 text-center text-gray-400 text-sm">
                        {t('customer.no_data')}
                    </div>
                )}

                {filteredList.map((item) => (
                    <div
                        key={item.id}
                        className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] cursor-pointer"
                        onClick={() => handleSelectFilter(item)}
                    >
                        <div className="flex items-center gap-2 text-sm text-000000e0">
                            <UserOutlined className="text-gray-400" />
                            <span>{item.name}</span>
                        </div>

                        <div
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()} // ❗ QUAN TRỌNG
                        >
                            <EyeOutlined className="text-gray-400 cursor-pointer" />

                            <DeleteOutlined
                                onClick={() => handleDelete(item.id)}
                                className="text-red-400 cursor-pointer"
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* FOOTER */}
            <div className="p-3 border-t dark:border-[#303030]">
                <Button
                    type="primary"
                    block
                    icon={<FilterOutlined />}
                    onClick={() => {
                        setOpenSavedFilter(false);
                        setOpenFilter(true);
                    }}
                >
                    {t('customer.filter')}
                </Button>
            </div>
        </div>
    );

    return (
        <div className="toolbar-customer flex flex-wrap items-center gap-2 mb-4 bg-[#f5f5f5] dark:bg-[#141414] rounded-md">
            {/* Search */}
            <div className="
                flex h-[36px] rounded-md overflow-hidden border 
                border-[#d9d9d9] dark:border-[#303030] 
                bg-white dark:bg-[#1f1f1f]

                w-full md:w-auto
            ">

                {/* Search */}
                <div className="flex items-center gap-2 px-3 flex-1 md:w-[260px]">
                    <SearchOutlined className="text-gray-500" />
                    <Input
                        bordered={false}
                        placeholder={t('customer.search_placeholder')}
                        defaultValue={initialFilters?.search}
                        onChange={(e) => onSearch?.(e.target.value)}
                        allowClear
                    />
                </div>
            </div>

            {/* Bộ lọc */}

            <Dropdown
                open={openSavedFilter}
                onOpenChange={setOpenSavedFilter}
                trigger={["click"]}
                dropdownRender={renderSavedFilter}
            >
                <div
                    className="
                        toolbar-item 
                        w-full md:w-[160px]
                        cursor-pointer text-[#000000e0] dark:text-white text-[14px]
                    "
                >
                    <FilterOutlined className="icon" />
                    <span>{t('customer.saved_filters')}</span>
                </div>
            </Dropdown>

            {/* Nguồn */}
            <div className="toolbar-item w-full md:w-[280px]">
                <FilterOutlined className="icon" />
                <TreeSelect
                    treeData={sourceTreeData}
                    value={selectedSources}
                    onChange={handleSourceChange}
                    treeCheckable
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    treeDefaultExpandAll
                    allowClear
                    bordered={false}
                    className="w-full"
                    placeholder={t('customer.select_source')}
                    maxTagCount="responsive"
                    loading={loadingSource}
                    treeNodeFilterProp="title"
                    showSearch
                />
            </div>

            {/* Thẻ trạng thái chăm sóc — API lọc theo kiểu VÀ (khách phải có đủ các thẻ đã chọn) */}
            <Tooltip title={t('customer.care_status_match_all')}>
                <div className="toolbar-item w-full md:w-[240px]">
                    <TagOutlined className="icon" />
                    <Select
                        mode="multiple"
                        bordered={false}
                        className="w-full"
                        placeholder={t('customer.select_care_status')}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        loading={loadingCareStatus}
                        defaultValue={initialFilters?.careStatusIds}
                        options={careStatusOptions.map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                            title: opt.label,
                        }))}
                        optionRender={(opt) => {
                            const found = careStatusOptions.find((c) => c.value === opt.value);
                            return <Tag color={found?.color} className="mr-0">{found?.label}</Tag>;
                        }}
                        onChange={(values) => onCareStatusChange?.(values as number[])}
                    />
                </div>
            </Tooltip>

            {/* Nhân viên trực page (in_charge_staff_id) */}
            <div className="toolbar-item w-full md:w-[220px]">
                <UserOutlined className="icon" />
                <Select
                    bordered={false}
                    placeholder={t('customer.select_page_staff') || 'Chọn nhân viên trực page'}
                    className="w-full"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={loadingPageStaff}
                    defaultValue={initialFilters?.pageStaffId}
                    options={pageStaffOptions}
                    onChange={(value) => onPageStaffChange?.(value as number | undefined)}
                />
            </div>

            {/* Người phụ trách */}
            <div className="toolbar-item w-full md:w-[220px]">
                <UserOutlined className="icon" />
                <Select
                    bordered={false}
                    placeholder={t('customer.select_responsible')}
                    className="w-full"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={loadingStaff}
                    defaultValue={initialFilters?.staffId}
                    options={staffOptions}
                    onChange={(value) => onStaffChange?.(value as number | undefined)}
                />
            </div>

            {/* Thời gian */}
            <div className="flex items-center gap-2 w-full md:w-auto">
                <div className="toolbar-item w-full md:w-[140px]">
                    <ClockCircleOutlined className="icon" />
                    <Select<PeriodMode>
                        bordered={false}
                        className="w-full"
                        value={periodType}
                        onChange={handlePeriodTypeChange}
                    >
                        <Option value="date">{t('customer.by_day')}</Option>
                        <Option value="week">{t('customer.by_week')}</Option>
                        <Option value="month">{t('customer.by_month')}</Option>
                        <Option value="year">{t('customer.by_year')}</Option>
                        <Option value="range">{t('customer.by_range') || "Khoảng thời gian"}</Option>
                    </Select>
                </div>
                {periodType === "range" ? (
                    // Khoảng tự chọn: rộng hơn để lọc từ ngày bất kỳ đến ngày bất kỳ
                    <RangePicker
                        className="w-full md:w-[260px]"
                        value={rangeValue as any}
                        onChange={handleRangeChange}
                        format="DD/MM/YYYY"
                        placeholder={[t('customer.from_date') || "Từ ngày", t('customer.to_date') || "Đến ngày"]}
                        allowClear
                    />
                ) : (
                    <DatePicker
                        className="w-full md:w-[180px]"
                        picker={periodType}
                        value={refDate}
                        onChange={handleRefDateChange}
                        placeholder={t('customer.select_time')}
                        allowClear
                    />
                )}
            </div>
            {/* Thêm mới */}
            {/* <Button onClick={() => setOpenCreate(true)} className="toolbar-item w-full md:w-[160px] !bg-[#3e91ab] !text-white flex items-center justify-center gap-1">
                <PlusOutlined className="icon !text-white" />
                Thêm mới
            </Button> */}
             <Button onClick={onCreate} className="toolbar-item w-full md:w-[160px] !bg-[#3e91ab] !text-white flex items-center justify-center gap-1">
                <PlusOutlined className="icon !text-white" />
                Thêm mới
            </Button>
            <FilterModal open={openFilter} onClose={() => setOpenFilter(false)} />
            {/* <CreateCustomerModal
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onSubmit={(values) => {
                    console.log("Submit:", values);
                }}
            /> */}
        </div>
    );
}